import React, { useState } from "react";
import Image from "next/image";
import { Linkedin } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Testimonial } from "@/types/cms";
import { trackTestimonialLinkedInClicked } from "@/lib/analytics";

export type { Testimonial };

interface TestimonialsCardProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Testimonial;
  priority?: boolean;
}

export default function TestimonialsCard({
  data,
  className,
  priority = false,
  ...rest
}: TestimonialsCardProps) {
  const [imgError, setImgError] = useState(false);

  // Fallback image if no profilePicture
  const imageUrl = data.profilePicture?.url || "/placeholder.svg";

  return (
    <div
      className={cn(
        "group relative h-full rounded-2xl overflow-hidden bg-neutral-900",
        className,
      )}
      {...rest}
    >
      {/* Full Height Background Image */}
      {imageUrl && !imgError ? (
        <Image
          src={imageUrl}
          alt={data.person || data.company || "Testimonial"}
          fill
          className="object-cover object-top transition-transform duration-500 group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, 33vw"
          priority={priority}
          onError={() => setImgError(true)}
        />
      ) : null}

      {/* Gradient Overlay - From middle to bottom */}
      <div className="absolute inset-0 bg-linear-to-t from-black via-black/80 to-transparent opacity-90" />

      {/* Glassmorphism Quote Container */}
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
        {/* Glass Layer */}
        <div className="backdrop-blur-md bg-black/30 border border-white/10 rounded-xl p-5 md:p-6 min-h-[240px] md:min-h-[240px] flex flex-col">
          {/* Name and Title - Above Quote */}
          <div className="mb-4 flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-semibold text-white line-clamp-1">
                {data.person}
              </h3>
              <p className="text-sm md:text-base text-[#D4A84B] line-clamp-1">
                {data.role},{" "}
                <span className="text-white/60">{data.company}</span>
              </p>
            </div>
            {data.linkedinLink && (
              <a
                href={data.linkedinLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackTestimonialLinkedInClicked(
                    data.person || "",
                    data.role || "",
                    data.company || "",
                  )
                }
                className="p-2 rounded-full bg-white/10 hover:bg-[#0A66C2] transition-colors duration-300 shrink-0 ml-2"
                aria-label={`${data.person}'s LinkedIn profile`}
              >
                <Linkedin className="w-4 h-4 text-white" />
              </a>
            )}
          </div>

          {/* Quote Mark */}
          <div className="text-[#D4A84B] text-4xl font-serif leading-none mb-2">
            "
          </div>

          {/* Quote Text */}
          <p className="text-sm md:text-base text-white/90 leading-relaxed italic line-clamp-4 md:line-clamp-5">
            {data.quote}
          </p>
        </div>
      </div>

      {/* Subtle Border Glow on Hover */}
      <div className="absolute inset-0 rounded-2xl border border-white/0 group-hover:border-[#D4A84B]/30 transition-colors duration-300 pointer-events-none" />
    </div>
  );
}
